// ── Design decisions ──
// Autocompletar de cliente sin librerías: un input, una lista (máx. 8) y
// teclado ↑ ↓ Enter Esc. El filtro corre sobre la lista `customers` que llega
// por props (sin red) para que la respuesta sea instantánea; primero rankea
// coincidencia por nombre y luego por teléfono, como hace searchCustomers del
// mock. Si no hay coincidencia exacta aparece la fila "＋ Crear «texto»" que
// despliega un mini-formulario inline (nombre prefilled + teléfono) y llama a
// onQuickCreate; al resolver, onChange recibe el cliente nuevo. Mientras no
// hay cliente se ve el chip ámbar "Sin cliente": ese es el bloqueo de cierre
// más común y tiene que saltar a la vista. El cierre por blur se posterga
// 150 ms para que el clic en una fila alcance a registrarse.
//
// `ambiance` (sólo lo pide el ExtractionDrawer) es piel y nada más: cambia las
// clases por los tokens de ambiance.css sin tocar el combobox ni el alta
// rápida. El chip "Sin cliente" mantiene su ámbar en las dos variantes, y el
// botón "Crear cliente" conserva su relleno fuerte actual.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, UserRound, X } from 'lucide-react';
import { normalizar } from './labels';
import { SinClienteChip } from './chips';

const MAX_LISTA = 8;

const inputBase =
  'rounded-md border border-slate-200 bg-[#f8fafc] px-2 py-1.5 text-xs text-slate-900 ' +
  'outline-none focus:border-[var(--brand-teal)] transition-colors';

const soloDigitos = (s) => String(s || '').replace(/\D/g, '');

export const filtrarClientes = (customers, q) => {
  const nq = normalizar(q);
  if (!nq) return (customers || []).slice(0, MAX_LISTA);
  const dq = soloDigitos(q);
  const porNombre = [];
  const porTelefono = [];
  (customers || []).forEach((c) => {
    if (normalizar(c.full_name).includes(nq)) porNombre.push(c);
    else if (dq.length >= 3 && soloDigitos(c.phone_number).includes(dq)) porTelefono.push(c);
  });
  return [...porNombre, ...porTelefono].slice(0, MAX_LISTA);
};

const ClientePicker = ({
  value,
  customers = [],
  onChange,
  onQuickCreate,
  disabled = false,
  id,
  ambiance = false,
}) => {
  const cls = {
    icono: ambiance ? 'shrink-0 amb-ico' : 'shrink-0 text-slate-400',
    input: ambiance ? 'amb-in w-56 min-w-0' : `${inputBase} w-56 min-w-0 disabled:opacity-50`,
    limpiar: ambiance
      ? 'amb-ibtn amb-ibtn--bare'
      : 'rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700',
    lista: ambiance
      ? 'absolute left-0 top-full z-30 mt-1 w-72 amb-pop'
      : 'absolute left-0 top-full z-30 mt-1 w-72 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg',
    formulario: ambiance
      ? 'amb-form'
      : 'flex flex-col gap-1.5 rounded-md border border-slate-200 bg-white p-2 shadow-sm',
    campo: ambiance ? 'amb-in w-full' : `${inputBase} w-full`,
    campoMono: ambiance ? 'amb-in amb-in--mono w-full' : `${inputBase} w-full font-mono`,
    error: ambiance ? 'amb-alert' : 'text-[11px] text-red-600',
    // El relleno fuerte del primario no cambia: Ambiance sólo aporta radio,
    // tipografía y sombra.
    crear: ambiance
      ? 'amb-btn amb-btn--primary min-h-[44px] flex-1 bg-slate-900 hover:bg-slate-800'
      : 'inline-flex min-h-[44px] flex-1 items-center justify-center rounded-md bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60',
    cancelar: ambiance
      ? 'amb-btn amb-btn--secondary min-h-[44px]'
      : 'inline-flex min-h-[44px] items-center rounded-md border border-slate-200 px-3 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-60',
  };
  const seleccionado = useMemo(
    () => (value ? customers.find((c) => c.id === value) || null : null),
    [customers, value]
  );
  const [texto, setTexto] = useState(seleccionado ? seleccionado.full_name : '');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const [creando, setCreando] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTelefono, setNuevoTelefono] = useState('');
  const [pendiente, setPendiente] = useState(false);
  const [errorCrear, setErrorCrear] = useState('');
  const inputRef = useRef(null);
  const blurTimer = useRef(null);

  // Si el valor cambia desde afuera, reflejarlo en el texto del input.
  useEffect(() => {
    setTexto(seleccionado ? seleccionado.full_name : '');
  }, [seleccionado]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const escribiendo = abierto && (!seleccionado || texto !== seleccionado.full_name);
  const lista = useMemo(
    () => filtrarClientes(customers, escribiendo ? texto : ''),
    [customers, texto, escribiendo]
  );
  const textoLimpio = texto.trim();
  const hayExacto = lista.some((c) => normalizar(c.full_name) === normalizar(textoLimpio));
  const mostrarCrear = Boolean(onQuickCreate) && textoLimpio.length > 0 && !hayExacto;
  const totalFilas = lista.length + (mostrarCrear ? 1 : 0);

  const elegir = (c) => {
    setAbierto(false);
    setTexto(c.full_name);
    onChange && onChange(c.id, c);
  };

  const limpiar = () => {
    setTexto('');
    onChange && onChange(null, null);
    inputRef.current?.focus();
  };

  const abrirCrear = () => {
    setNuevoNombre(textoLimpio);
    setNuevoTelefono('');
    setErrorCrear('');
    setCreando(true);
    setAbierto(false);
  };

  const cancelarCrear = () => {
    setCreando(false);
    setErrorCrear('');
    inputRef.current?.focus();
  };

  const confirmarCrear = async (e) => {
    e && e.preventDefault();
    if (pendiente) return;
    setErrorCrear('');
    setPendiente(true);
    try {
      const nuevo = await onQuickCreate({
        full_name: nuevoNombre.trim(),
        phone_number: nuevoTelefono.trim(),
      });
      setCreando(false);
      setTexto(nuevo?.full_name || nuevoNombre.trim());
      onChange && onChange(nuevo?.id ?? null, nuevo || null);
    } catch (err) {
      setErrorCrear(err?.message || 'No se pudo crear el cliente. Intente de nuevo.');
    } finally {
      setPendiente(false);
    }
  };

  const onKeyDown = (e) => {
    if (!abierto && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setAbierto(true);
      setResaltado(0);
      return;
    }
    if (!abierto) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (totalFilas > 0) setResaltado((i) => (i + 1) % totalFilas);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (totalFilas > 0) setResaltado((i) => (i - 1 + totalFilas) % totalFilas);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (resaltado < lista.length) elegir(lista[resaltado]);
      else if (mostrarCrear) abrirCrear();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setAbierto(false);
      setTexto(seleccionado ? seleccionado.full_name : '');
    }
  };

  return (
    <div className="relative inline-flex min-w-0 flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <UserRound size={13} className={cls.icono} aria-hidden="true" />
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-label="Cliente del paquete"
          aria-expanded={abierto}
          aria-autocomplete="list"
          aria-controls={id ? `${id}-lista` : undefined}
          title="Buscar cliente (↑ ↓ para moverse, Enter para elegir, Esc para cerrar)"
          placeholder="Buscar cliente por nombre o teléfono"
          value={texto}
          disabled={disabled || creando}
          onChange={(e) => {
            setTexto(e.target.value);
            setResaltado(0);
            setAbierto(true);
          }}
          onFocus={() => {
            clearTimeout(blurTimer.current);
            setAbierto(true);
          }}
          onBlur={() => {
            blurTimer.current = setTimeout(() => {
              setAbierto(false);
              // Si borró el texto sin elegir, se mantiene el valor anterior.
              setTexto(seleccionado ? seleccionado.full_name : '');
            }, 150);
          }}
          onKeyDown={onKeyDown}
          className={cls.input}
        />
        {seleccionado && !disabled && (
          <button
            type="button"
            onClick={limpiar}
            aria-label="Quitar cliente"
            title="Quitar cliente"
            className={cls.limpiar}
          >
            <X size={12} aria-hidden="true" />
          </button>
        )}
        {!seleccionado && <SinClienteChip />}
      </div>

      {abierto && !creando && totalFilas > 0 && (
        <ul
          id={id ? `${id}-lista` : undefined}
          role="listbox"
          aria-label="Clientes sugeridos"
          className={cls.lista}
        >
          {lista.map((c, i) => (
            <li
              key={c.id}
              role="option"
              aria-selected={i === resaltado}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setResaltado(i)}
              onClick={() => elegir(c)}
              className={
                ambiance
                  ? `amb-pop-row${i === resaltado ? ' amb-pop-row--on' : ''}`
                  : `flex cursor-pointer items-center justify-between gap-2 px-2.5 py-1.5 text-xs ${
                      i === resaltado ? 'bg-[var(--brand-teal-soft)] text-slate-900' : 'text-slate-700'
                    }`
              }
            >
              <span className="truncate font-medium">{c.full_name}</span>
              <span
                className={ambiance ? 'amb-pop-meta' : 'shrink-0 font-mono text-[10px] text-slate-400'}
              >
                {c.phone_number || c.id}
              </span>
            </li>
          ))}
          {mostrarCrear && (
            <li
              role="option"
              aria-selected={resaltado === lista.length}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setResaltado(lista.length)}
              onClick={abrirCrear}
              className={
                ambiance
                  ? `amb-pop-row amb-pop-row--nuevo${
                      resaltado === lista.length ? ' amb-pop-row--on' : ''
                    }`
                  : `flex cursor-pointer items-center gap-1.5 border-t border-slate-200 px-2.5 py-1.5 text-xs font-medium ${
                      resaltado === lista.length
                        ? 'bg-[var(--brand-teal-soft)] text-slate-900'
                        : 'text-[var(--brand-teal)]'
                    }`
              }
            >
              <Plus size={12} aria-hidden="true" />
              {`Crear «${textoLimpio}»`}
            </li>
          )}
        </ul>
      )}

      {creando && (
        <form
          onSubmit={confirmarCrear}
          aria-label="Crear cliente nuevo"
          className={cls.formulario}
        >
          <input
            type="text"
            autoFocus
            aria-label="Nombre del cliente nuevo"
            placeholder="Nombre completo"
            value={nuevoNombre}
            onChange={(e) => setNuevoNombre(e.target.value)}
            className={cls.campo}
          />
          <input
            type="tel"
            aria-label="Teléfono del cliente nuevo"
            placeholder="+503 7XXX-XXXX"
            value={nuevoTelefono}
            onChange={(e) => setNuevoTelefono(e.target.value)}
            className={cls.campoMono}
          />
          {errorCrear && (
            <p role="alert" className={cls.error}>
              {errorCrear}
            </p>
          )}
          <div className="flex items-center gap-1.5">
            <button
              type="submit"
              disabled={pendiente}
              className={cls.crear}
            >
              {pendiente ? 'Creando…' : 'Crear cliente'}
            </button>
            <button
              type="button"
              onClick={cancelarCrear}
              disabled={pendiente}
              className={cls.cancelar}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ClientePicker;
